import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ChefHat, 
  Carrot, 
  Factory, 
  ArrowRight,
  Clock,
  UtensilsCrossed,
  Calendar,
  CalendarDays,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { format, getDay, addDays, startOfWeek, addWeeks, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, parseISO } from 'date-fns';
import { getRotationWeekNumber, getDaySchedule, getCustomDayRecipe } from '../utils/rotationUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KITCHEN_IMAGE = "https://images.unsplash.com/photo-1767785990437-dfe1fe516fe8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxjaGVmJTIwY29va2luZyUyMGNvbW1lcmNpYWwlMjBraXRjaGVufGVufDB8fHx8MTc3MTI4MzMzMnww&ixlib=rb-4.1.0&q=85";

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentPlans, setRecentPlans] = useState([]);
  const [todaysMenu, setTodaysMenu] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regularRotation, setRegularRotation] = useState(null);
  const [specialRotation, setSpecialRotation] = useState(null);
  const [customRotations, setCustomRotations] = useState([]);
  const [calendarView, setCalendarView] = useState('week'); // 'week' or 'month'
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [rotationStartDate, setRotationStartDate] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate which week number a date falls into
  const getWeekNumber = (date, totalWeeks) => getRotationWeekNumber(date, rotationStartDate, totalWeeks);

  const fetchData = async () => {
    try {
      const [statsRes, plansRes, recipesRes, regularRotRes, specialRotRes, customRotRes, settingsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/production/plans`),
        axios.get(`${API}/recipes`),
        axios.get(`${API}/rotations/regular`),
        axios.get(`${API}/rotations/special`),
        axios.get(`${API}/rotations/custom`),
        axios.get(`${API}/rotation-settings`)
      ]);
      
      setStats(statsRes.data);
      setRecentPlans(plansRes.data.slice(0, 5));
      setRecipes(recipesRes.data);
      
      // Set rotation start date
      if (settingsRes.data.rotation_start_date) {
        setRotationStartDate(parseISO(settingsRes.data.rotation_start_date));
      }
      
      // Store rotations for calendar preview
      const regRotation = regularRotRes.data.length > 0 ? regularRotRes.data[0] : null;
      const specRotation = specialRotRes.data.length > 0 ? specialRotRes.data[0] : null;
      if (regRotation) setRegularRotation(regRotation);
      if (specRotation) setSpecialRotation(specRotation);
      setCustomRotations(customRotRes.data.filter(r => r.active));
      
      // Figure out today's menu from rotation
      const today = new Date();
      const dayOfWeek = getDay(today); // 0 = Sunday, 1 = Monday, etc.
      const dayName = WEEKDAY_NAMES[dayOfWeek];
      
      // Calculate current week number
      const totalWeeks = regRotation?.rotation_length || 1;
      const startDate = settingsRes.data.rotation_start_date ? parseISO(settingsRes.data.rotation_start_date) : null;
      const currentWeekNum = getRotationWeekNumber(today, startDate, totalWeeks);
      
      let menuItems = [];
      let isSpecialDay = false;
      
      // Check if today is in the special rotation
      if (specRotation) {
        const selectedDays = specRotation.days || ['Sunday'];
        
        if (selectedDays.includes(dayName)) {
          isSpecialDay = true;
          const schedule = specRotation.schedule || {};
          const specWeekNum = getRotationWeekNumber(today, startDate, specRotation.rotation_length || 1);
          const dayMenu = getDaySchedule(schedule, `Week${specWeekNum}`, dayName);
          if (dayMenu.main) menuItems.push({ type: 'Main', name: dayMenu.main });
          if (dayMenu.roti) menuItems.push({ type: 'Roti', name: dayMenu.roti });
          if (dayMenu.raita) menuItems.push({ type: 'Raita', name: dayMenu.raita });
          if (dayMenu.side) menuItems.push({ type: 'Side', name: dayMenu.side });
        }
      }
      
      // Check regular rotation for weekdays (or if special rotation doesn't have this day)
      if (regRotation && menuItems.length === 0) {
        const schedule = regRotation.schedule || {};
        const selectedDays = regRotation.weekdays || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
        
        if (selectedDays.includes(dayName)) {
          const dayMenu = getDaySchedule(schedule, `Week${currentWeekNum}`, dayName);
          if (dayMenu.sabzi) menuItems.push({ type: 'Sabzi', name: dayMenu.sabzi });
          if (dayMenu.daal) menuItems.push({ type: 'Daal', name: dayMenu.daal });
          if (dayMenu.salad) menuItems.push({ type: 'Salad', name: dayMenu.salad });
        }
      }
      
      setTodaysMenu({
        dayName,
        items: menuItems,
        isSpecialDay,
        weekNum: currentWeekNum
      });
      
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Get menu for a specific date
  const getDayMenu = (date) => {
    const dayOfWeek = getDay(date);
    const dayName = WEEKDAY_NAMES[dayOfWeek];
    const weekNum = getWeekNumber(date, regularRotation?.rotation_length || 1);
    const weekKey = `Week${weekNum}`;
    
    // Saturday is off
    if (dayOfWeek === 6) {
      return { type: 'off', items: [], weekNum };
    }
    
    let items = [];
    let type = 'regular';
    
    // Check special rotation
    if (specialRotation) {
      const selectedDays = specialRotation.days || ['Sunday'];
      if (selectedDays.includes(dayName)) {
        type = 'special';
        const specWeekKey = `Week${getWeekNumber(date, specialRotation.rotation_length || 1)}`;
        const dayMenu = getDaySchedule(specialRotation.schedule, specWeekKey, dayName);
        if (dayMenu.main) items.push({ label: 'Main', value: dayMenu.main });
        if (dayMenu.roti) items.push({ label: 'Roti', value: dayMenu.roti });
      }
    }
    
    // Check regular rotation
    if (items.length === 0 && regularRotation) {
      const selectedDays = regularRotation.weekdays || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
      if (selectedDays.includes(dayName)) {
        const dayMenu = getDaySchedule(regularRotation.schedule, weekKey, dayName);
        if (dayMenu.sabzi) items.push({ label: 'Sabzi', value: dayMenu.sabzi });
        if (dayMenu.daal) items.push({ label: 'Daal', value: dayMenu.daal });
      }
    }
    
    // Add custom rotation items
    customRotations.forEach(rotation => {
      if (rotation.days?.includes(dayName)) {
        const rotWeeks = rotation.rotation_length || 1;
        const rotWeekNum = getWeekNumber(date, rotWeeks);
        const recipe = getCustomDayRecipe(rotation.schedule, `Week${rotWeekNum}`, dayName);
        if (recipe) {
          items.push({ label: rotation.slot_name, value: recipe, isCustom: true });
        }
      }
    });
    
    return { type, items };
  };

  // Get week days starting from currentWeekStart
  const getWeekDays = () => {
    return Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  };

  // Get month days
  const getMonthDays = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  };

  const getRecipeByName = (name) => {
    return recipes.find(r => r.recipe_name === name);
  };

  const handleRecipeClick = (recipeName) => {
    const recipe = getRecipeByName(recipeName);
    if (recipe) {
      navigate(`/recipes/${recipe.id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const quickActions = [
    { 
      label: 'Add Recipe', 
      icon: ChefHat, 
      path: '/recipes/new', 
      color: 'bg-[#124242]',
      textColor: 'text-white'
    },
    { 
      label: 'Generate Production', 
      icon: Factory, 
      path: '/production', 
      color: 'bg-[#faa023]',
      textColor: 'text-black'
    },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-800 tracking-tight">
            Dashboard
          </h1>
          <p className="text-stone-500 mt-1">
            {format(new Date(), 'EEEE, MMMM d, yyyy')}
          </p>
        </div>
      </div>

      {/* Today's Menu - Featured Section */}
      <div 
        className="virsa-card overflow-hidden border-2 border-[#124242]"
        style={{
          background: 'linear-gradient(135deg, #124242 0%, #0d3232 100%)'
        }}
        data-testid="todays-menu"
      >
        <div className="p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <UtensilsCrossed size={28} />
            <h2 className="text-2xl font-bold">
              Today's Menu - {todaysMenu?.dayName}
              {todaysMenu?.isSpecialDay && <span className="ml-2 text-[#faa023]">★ Special</span>}
            </h2>
          </div>
          
          {todaysMenu?.items?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {todaysMenu.items.map((item, index) => {
                const recipe = getRecipeByName(item.name);
                return (
                  <button
                    key={`${item.type}-${item.name}`}
                    onClick={() => handleRecipeClick(item.name)}
                    className="bg-white/10 hover:bg-white/20 backdrop-blur rounded-xl p-4 text-left transition-all cursor-pointer group"
                    data-testid={`todays-menu-item-${index}`}
                  >
                    <p className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-1">
                      {item.type}
                    </p>
                    <p className="text-lg font-bold text-white group-hover:text-[#faa023] transition-colors">
                      {item.name}
                    </p>
                    {recipe && (
                      <p className="text-xs text-white/60 mt-2 flex items-center gap-1">
                        <ArrowRight size={12} />
                        Click to view recipe
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="bg-white/10 rounded-xl p-6 text-center">
              <p className="text-white/80 mb-2">No menu set for today</p>
              <button
                onClick={() => navigate(todaysMenu?.isSpecialDay ? '/rotation/special' : '/rotation/regular')}
                className="text-[#faa023] hover:underline text-sm font-semibold"
              >
                Set up your rotation →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Quick Access - Recipes Button */}
      <button
        onClick={() => navigate('/recipes')}
        className="w-full virsa-card p-4 flex items-center justify-between hover:border-[#124242] hover:shadow-md transition-all group"
        data-testid="quick-recipes-btn"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#124242] flex items-center justify-center">
            <ChefHat size={24} className="text-white" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-bold text-stone-800 group-hover:text-[#124242] transition-colors">
              Recipes
            </h3>
            <p className="text-sm text-stone-500">
              {stats?.recipes_count || 0} recipes available
            </p>
          </div>
        </div>
        <ArrowRight size={24} className="text-stone-400 group-hover:text-[#124242] group-hover:translate-x-1 transition-all" />
      </button>

      {/* Calendar Preview - Weekly/Monthly View */}
      <div className="virsa-card overflow-hidden" data-testid="calendar-preview">
        <div className="p-4 border-b border-stone-200">
          {/* Centered Title */}
          <div className="flex flex-col items-center mb-4">
            <div className="flex items-center gap-2 mb-1">
              <CalendarDays size={24} className="text-[#124242]" />
              <h2 className="text-xl font-bold text-stone-800">Upcoming Menu</h2>
            </div>
            {/* View Toggle */}
            <div className="flex bg-stone-100 rounded-lg p-1 mt-2">
              <button
                onClick={() => setCalendarView('week')}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  calendarView === 'week' ? 'bg-white text-[#124242] shadow-sm' : 'text-stone-600'
                }`}
              >
                Week
              </button>
              <button
                onClick={() => setCalendarView('month')}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  calendarView === 'month' ? 'bg-white text-[#124242] shadow-sm' : 'text-stone-600'
                }`}
              >
                Month
              </button>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                if (calendarView === 'week') {
                  setCurrentWeekStart(addWeeks(currentWeekStart, -1));
                } else {
                  setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)));
                }
              }}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} className="text-stone-600" />
            </button>
            <span className="font-semibold text-stone-700">
              {calendarView === 'week' 
                ? `${format(currentWeekStart, 'MMM d')} - ${format(addDays(currentWeekStart, 6), 'MMM d, yyyy')}`
                : format(currentMonth, 'MMMM yyyy')
              }
            </span>
            <button
              onClick={() => {
                if (calendarView === 'week') {
                  setCurrentWeekStart(addWeeks(currentWeekStart, 1));
                } else {
                  setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)));
                }
              }}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <ChevronRight size={20} className="text-stone-600" />
            </button>
          </div>
        </div>

        {/* Weekly View - Portrait/Vertical Layout */}
        {calendarView === 'week' && (
          <div className="divide-y divide-stone-100">
            {getWeekDays().map((date, idx) => {
              const menu = getDayMenu(date);
              const isToday = isSameDay(date, new Date());
              const isSaturday = getDay(date) === 6;
              const isPast = date < new Date() && !isToday;
              
              return (
                <div 
                  key={date.toISOString()} 
                  className={`p-4 ${
                    isToday ? 'bg-[#124242]/5' : 
                    isSaturday ? 'bg-stone-50' : 
                    isPast ? 'opacity-50' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Date Column */}
                    <div className={`flex-shrink-0 w-14 text-center rounded-lg py-2 ${
                      isToday ? 'bg-[#124242] text-white' : 'bg-stone-100'
                    }`}>
                      <p className={`text-xs font-medium ${isToday ? 'text-white/80' : 'text-stone-500'}`}>
                        {format(date, 'EEE')}
                      </p>
                      <p className={`text-xl font-bold ${isToday ? 'text-white' : 'text-stone-700'}`}>
                        {format(date, 'd')}
                      </p>
                    </div>
                    
                    {/* Menu Items */}
                    <div className="flex-1 min-w-0">
                      {isSaturday ? (
                        <div className="flex items-center h-full py-2">
                          <span className="text-stone-400 text-sm italic">Day Off</span>
                        </div>
                      ) : menu.items.length > 0 ? (
                        <div className="space-y-1.5">
                          {menu.items.map((item, i) => (
                            <div key={`${item.label}-${item.value}`} className="flex items-center gap-2">
                              <span className={`text-xs font-semibold uppercase w-16 flex-shrink-0 ${
                                item.isCustom ? 'text-purple-500' : 
                                menu.type === 'special' ? 'text-[#d6922b]' : 'text-stone-400'
                              }`}>
                                {item.label}
                              </span>
                              <span className={`text-sm font-medium truncate ${
                                item.isCustom ? 'text-purple-700' : 
                                menu.type === 'special' ? 'text-[#b8860b]' : 'text-stone-700'
                              }`}>
                                {item.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center h-full py-2">
                          <span className="text-stone-400 text-sm italic">No menu set</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Monthly View */}
        {calendarView === 'month' && (
          <div className="p-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-semibold text-stone-500 py-1">
                  {day}
                </div>
              ))}
            </div>
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Padding for first week */}
              {Array.from({ length: getDay(startOfMonth(currentMonth)) }, (_, i) => (
                <div key={`pad-${i}`} className="p-1 min-h-[60px]" />
              ))}
              {/* Actual days */}
              {getMonthDays().map((date, idx) => {
                const menu = getDayMenu(date);
                const isToday = isSameDay(date, new Date());
                const isSaturday = getDay(date) === 6;
                
                return (
                  <div 
                    key={date.toISOString()}
                    className={`rounded p-1 min-h-[60px] ${
                      isToday ? 'bg-[#124242]/10 ring-1 ring-[#124242]' : 
                      isSaturday ? 'bg-stone-50' : ''
                    }`}
                  >
                    <p className={`text-xs font-semibold mb-0.5 ${isToday ? 'text-[#124242]' : 'text-stone-600'}`}>
                      {format(date, 'd')}
                    </p>
                    {!isSaturday && menu.items.length > 0 && (
                      <div className="space-y-0.5">
                        {menu.items.slice(0, 2).map((item, i) => (
                          <p key={`${item.label}-${item.value}`} className={`text-[10px] truncate ${
                            item.isCustom ? 'text-purple-600' : 
                            menu.type === 'special' ? 'text-[#d6922b]' : 'text-stone-500'
                          }`}>
                            {item.value}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions - Right after Today's Menu */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {quickActions.map((action) => (
          <button
            key={action.label}
            onClick={() => navigate(action.path)}
            className={`${action.color} ${action.textColor} rounded-xl p-5 flex items-center gap-4 hover:opacity-90 transition-opacity btn-press`}
            data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <action.icon size={24} />
            <span className="font-bold text-lg">{action.label}</span>
            <ArrowRight size={20} className="ml-auto" />
          </button>
        ))}
      </div>

      {/* Stats Grid - Only Recipes and Ingredients */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div 
          className="virsa-card p-6 cursor-pointer hover:shadow-lg transition-shadow" 
          data-testid="stat-recipes"
          onClick={() => navigate('/recipes')}
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-[#124242]/10 flex items-center justify-center">
              <ChefHat className="text-[#124242]" size={28} />
            </div>
            <div>
              <p className="text-3xl font-bold text-stone-800">{stats?.recipes_count || 0}</p>
              <p className="text-sm text-stone-500">Total Recipes</p>
            </div>
            <ArrowRight size={20} className="ml-auto text-stone-300" />
          </div>
        </div>

        <div 
          className="virsa-card p-6 cursor-pointer hover:shadow-lg transition-shadow" 
          data-testid="stat-ingredients"
          onClick={() => navigate('/ingredients')}
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-[#faa023]/20 flex items-center justify-center">
              <Carrot className="text-[#d6922b]" size={28} />
            </div>
            <div>
              <p className="text-3xl font-bold text-stone-800">{stats?.ingredients_count || 0}</p>
              <p className="text-sm text-stone-500">Total Ingredients</p>
            </div>
            <ArrowRight size={20} className="ml-auto text-stone-300" />
          </div>
        </div>
      </div>

      {/* Recent Production */}
      <div className="virsa-card p-6">
        <h2 className="text-xl font-bold text-stone-800 mb-4">
          Recent Production
        </h2>
        {recentPlans.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentPlans.map((plan) => (
              <div
                key={plan.id}
                className="p-4 rounded-lg bg-stone-50 hover:bg-stone-100 cursor-pointer transition-colors"
                onClick={() => navigate(`/production?plan=${plan.id}`)}
                data-testid={`recent-plan-${plan.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-stone-800">{format(new Date(plan.date), 'MMM d, yyyy')}</p>
                    <p className="text-sm text-stone-500">{plan.tiffin_count} tiffins</p>
                  </div>
                  <div className="badge badge-teal">
                    {plan.dishes?.length || 0} dishes
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state py-8">
            <Clock size={32} className="text-stone-300 mb-2" />
            <p className="text-stone-500">No production plans yet</p>
            <button
              onClick={() => navigate('/production')}
              className="mt-2 text-sm text-[#124242] hover:underline"
            >
              Generate your first plan
            </button>
          </div>
        )}
      </div>

      {/* Welcome Banner */}
      {(stats?.recipes_count === 0 && stats?.ingredients_count === 0) && (
        <div 
          className="virsa-card overflow-hidden"
          style={{
            backgroundImage: `linear-gradient(rgba(26, 92, 92, 0.9), rgba(26, 92, 92, 0.95)), url(${KITCHEN_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="p-8 text-white">
            <h2 className="text-3xl font-bold mb-2">
              Welcome to VPrep Tiffin Management
            </h2>
            <p className="text-white/80 mb-6 max-w-2xl">
              Get started by adding your ingredients and recipes. Then set up your weekly rotation 
              to automate your menu planning and production.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/ingredients')}
                className="btn-secondary"
              >
                <Carrot size={20} />
                Add Ingredients
              </button>
              <button
                onClick={() => navigate('/recipes/new')}
                className="bg-white text-[#124242] px-6 py-3 rounded-lg font-semibold hover:bg-white/90 transition-colors flex items-center gap-2"
              >
                <ChefHat size={20} />
                Add Recipes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
